/**
 * rtStructModule.js — Импорт и отображение DICOM RT Structure Set (RTSTRUCT)
 * Mikhmel Cornerstone4D DICOM Viewer Pro — Этап 1 плана СРХ (радиохирургия)
 *
 * НАЗНАЧЕНИЕ
 *   Читает RTSTRUCT (SOP Class 1.2.840.10008.5.1.4.1.1.481.3), извлекает
 *   контуры структур (мишень, критические органы) и рисует их поверх
 *   существующих 2D/MPR-вьюпортов Cornerstone3D. Только просмотр —
 *   ни расчёта дозы, ни клинического планирования (так и задумано).
 *
 * ПОЧЕМУ SVG-ОВЕРЛЕЙ, А НЕ СЕГМЕНТАЦИЯ CORNERSTONE
 *   1. Не зависит от version-fragile API сегментации CS 5.x (часть инструментов
 *      у нас молча отваливается — см. toolManager.js:56-59 и README).
 *   2. Точно повторяет проверенный паттерн gridOverlay.js: свой <svg> на
 *      vp.element, перерисовка по CAMERA_MODIFIED / IMAGE_RENDERED / ResizeObserver.
 *   3. Работает во всех окнах MPR сразу: в плоскости среза рисуем сам контур,
 *      на ортогональных плоскостях — линию пересечения структуры с плоскостью.
 *
 * СИСТЕМА КООРДИНАТ
 *   ContourData в RTSTRUCT заданы в пациентских координатах (LPS, мм) в кадре
 *   Frame of Reference. Мировые координаты Cornerstone3D — тот же пациентский
 *   кадр (из ImagePositionPatient/ImageOrientationPatient), поэтому точки
 *   ложатся напрямую через vp.worldToCanvas(). ВАЖНО: RTSTRUCT и загруженный
 *   том должны ссылаться на один Frame of Reference (обычно так и есть, если
 *   структуры рисовались по этой же серии КТ/МРТ).
 */

import { log } from '../utils/logger.js';
import { cornerstone, getRenderingEngine, getViewportIds } from '../core/viewportState.js';
import { registerAPIs } from '../core/viewerAPI.js';
import { on as onEvent, EVENTS } from '../core/eventBus.js';
// [12.0.25] Открытие раздела справа — одно на RT и «Качество»: с прокруткой
// к началу и разовой отметкой вместо постоянного перелива.
import { открытьРазделСправа, закрытьСайдбарСправа, следитьЗаКнопкой } from '../ui/rightSection.js';

/** Версия модуля СРХ. Поднимается вместе с версией приложения на каждом этапе. */
export const RT_MODULE_VERSION = '3.0 (Этапы 1-2: RTSTRUCT + оконтуривание мишени)';

// ─── Палитра для структур без собственного ROIDisplayColor ───
const _PALETTE = [
  [255, 90, 90], [90, 200, 255], [120, 230, 130], [255, 200, 70],
  [220, 130, 255], [255, 150, 200], [130, 255, 230], [200, 200, 120],
];

// ─── Состояние модуля ───
const _state = {
  structures: [],          // [{ roiNumber, name, type, color:[r,g,b], visible, contours:[...] }]
  frameOfReferenceUID: null,
  loaded: false,
  sliceTolMm: 2.0,         // допуск "срез в плоскости контура" (± мм)
  showCrossSection: true,  // рисовать линии пересечения на ортогональных плоскостях
  ghostRangeMm: 8,         // на сколько мм вокруг показывать соседние контуры призраком
};

// ═══════════════════════════════════════════════════════════════
// Векторная математика (минимальный набор)
// ═══════════════════════════════════════════════════════════════
const _sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const _dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const _cross = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const _norm = (a) => {
  const L = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / L, a[1] / L, a[2] / L];
};

function _toArray(x) {
  if (x == null) return [];
  return Array.isArray(x) ? x : [x];
}

/** Центр масс вершин контура. */
function _centroid(pts) {
  let x = 0, y = 0, z = 0;
  for (const p of pts) { x += p[0]; y += p[1]; z += p[2]; }
  const n = pts.length || 1;
  return [x / n, y / n, z / n];
}

/**
 * Медианный шаг между соседними контурами структуры вдоль её нормали.
 * Нужен, чтобы допуск «контур в плоскости среза» подстраивался под реальную
 * толщину среза, а не был захардкожен. Возвращает 0, если оценить нельзя.
 */
function _estimateSpacing(contours) {
  const ref = contours.find(c => c.normal)?.normal;
  if (!ref || contours.length < 2) return 0;
  const offs = contours
    .filter(c => c.normal && Math.abs(_dot(c.normal, ref)) > 0.9)
    .map(c => _dot(c.centroid, ref))
    .sort((a, b) => a - b);
  if (offs.length < 2) return 0;
  const diffs = [];
  for (let i = 1; i < offs.length; i++) {
    const d = offs[i] - offs[i - 1];
    if (d > 1e-3) diffs.push(d);
  }
  if (!diffs.length) return 0;
  diffs.sort((a, b) => a - b);
  return diffs[Math.floor(diffs.length / 2)]; // медиана устойчива к пропускам срезов
}

/** Нормаль плоскости планарного контура (или null для вырожденного). */
function _contourNormal(pts) {
  if (pts.length < 3) return null;
  const p0 = pts[0];
  for (let i = 1; i < pts.length - 1; i++) {
    const v1 = _sub(pts[i], p0);
    const v2 = _sub(pts[i + 1], p0);
    const n = _cross(v1, v2);
    if (Math.hypot(n[0], n[1], n[2]) > 1e-6) return _norm(n);
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════
// ПАРСЕР RTSTRUCT (dcmjs + запасной кодек)
// ═══════════════════════════════════════════════════════════════

/**
 * Разбирает RTSTRUCT из ArrayBuffer.
 *
 * [7.0.20] Извлечение структур вынесено в rtStructIOCore (чистое ядро под
 * тестами — общий код с экспортом). Плюс запасной путь: раньше без dcmjs
 * импорт умирал («npm install dcmjs»), теперь файл разберёт наш кодек
 * (Explicit/Implicit VR LE, undefined-length — то, что пишут станции).
 *
 * @param {ArrayBuffer} arrayBuffer
 * @returns {Promise<{structures:Array, frameOfReferenceUID:string}>}
 */
export async function parseRTStruct(arrayBuffer) {
  let ds = null, via = 'dcmjs';
  try {
    const dcmjsModule = await import('dcmjs');
    const dcmjs = dcmjsModule.default || dcmjsModule;
    const DicomMessage = dcmjs.data?.DicomMessage || dcmjs.DicomMessage;
    const DicomMetaDictionary = dcmjs.data?.DicomMetaDictionary || dcmjs.DicomMetaDictionary;
    if (!DicomMessage || !DicomMetaDictionary) throw new Error('нет DicomMessage/DicomMetaDictionary');
    const dicomData = DicomMessage.readFile(arrayBuffer);
    ds = DicomMetaDictionary.naturalizeDataset(dicomData.dict);
  } catch (e) {
    log.warn(`[RTSTRUCT] dcmjs не разобрал файл (${e.message}) — пробую запасной кодек`);
    via = 'запасной кодек';
    const { readPart10, naturalizeLite } = await import('./rtDicomCodecCore.js');
    const bytes = arrayBuffer instanceof Uint8Array ? arrayBuffer : new Uint8Array(arrayBuffer);
    ds = naturalizeLite(readPart10(bytes).dataset);
  }

  if (ds.Modality && ds.Modality !== 'RTSTRUCT') {
    log.warn(`[RTSTRUCT] Ожидался Modality=RTSTRUCT, получено "${ds.Modality}" — пробую разобрать всё равно`);
  }

  const { structuresFromNaturalized } = await import('./rtStructIOCore.js');
  const res = structuresFromNaturalized(ds, { palette: _PALETTE });
  log.info(`[RTSTRUCT] Разобран через ${via}: структур ${res.structures.length}`);
  return res;
}

// ═══════════════════════════════════════════════════════════════
// ЗАГРУЗКА
// ═══════════════════════════════════════════════════════════════

export async function loadRTStructFromArrayBuffer(buf) {
  const { structures, frameOfReferenceUID } = await parseRTStruct(buf);
  _state.structures = structures;
  _state.frameOfReferenceUID = frameOfReferenceUID;
  _state.loaded = true;
  log.info(`[RTSTRUCT] Загружено структур: ${structures.length}`);
  _renderPanel();
  renderRTStructOverlays();
  return structures;
}

export async function loadRTStructFromFile(file) {
  const buf = await file.arrayBuffer();
  return loadRTStructFromArrayBuffer(buf);
}

export function clearRTStruct() {
  _state.structures = [];
  _state.frameOfReferenceUID = null;
  _state.loaded = false;
  _clearAllSvg();
  _renderPanel();
}

// ═══════════════════════════════════════════════════════════════
// ОТРИСОВКА КОНТУРОВ ПОВЕРХ ВЬЮПОРТОВ
// ═══════════════════════════════════════════════════════════════

function _svgFor(el) {
  let svg = el.querySelector(':scope > svg.vp-rtstruct');
  if (!svg) {
    svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'vp-rtstruct');
    svg.setAttribute('style',
      'position:absolute;left:0;top:0;width:100%;height:100%;' +
      'pointer-events:none;z-index:12;overflow:visible;');
    el.appendChild(svg);
  }
  return svg;
}

function _clearAllSvg() {
  try {
    const re = getRenderingEngine();
    for (const vpId of (getViewportIds() || [])) {
      const el = re?.getViewport(vpId)?.element;
      const svg = el?.querySelector(':scope > svg.vp-rtstruct');
      if (svg) svg.innerHTML = '';
    }
  } catch (e) { /* пусто */ }
}

/**
 * Отрисовать одну структуру в одном вьюпорте.
 * @returns {string} SVG-разметка (path/line)
 */
function _drawStructure(vp, st, N, P) {
  const rgb = `rgb(${st.color[0]},${st.color[1]},${st.color[2]})`;
  const fill = `rgba(${st.color[0]},${st.color[1]},${st.color[2]},0.12)`;

  // [1.1] Допуск подстраивается под реальный шаг контуров структуры.
  // Раньше был жёстко 2 мм и мерился по МАКСИМАЛЬНОМУ отклонению вершин —
  // на косых сериях (напр. OAx 9°) контур радиусом R даёт отклонение R·sin(9°),
  // что превышало допуск, и структура молча пропадала. Теперь меряем отклонение
  // ЦЕНТРА контура: оно не зависит от радиуса и наклона.
  const tol = st.spacing > 0
    ? Math.max(st.spacing * 0.6, 0.6)
    : (_state.sliceTolMm || 2.0);

  let out = '';
  const crossSegs = [];   // сечения для сборки сплошного контура
  let allPaired = true;   // все ли контуры дали ровно 2 точки пересечения

  for (const c of st.contours) {
    const pts = c.points;
    const nPts = pts.length;

    // Отклонение центра контура от плоскости среза + косинус между нормалями
    const dCenter = _dot(_sub(c.centroid || _centroid(pts), P), N);
    const parallel = c.normal ? Math.abs(_dot(c.normal, N)) : 0;

    // [2.8] Контуры с СОСЕДНИХ срезов показываем призраком.
    // Без этого, пролистав срез, врач теряет из виду всё, что уже обвёл:
    // непонятно, где ты находишься и что осталось. В планирующих станциях
    // соседние контуры показывают всегда — это основной ориентир.
    if (parallel > 0.9 && Math.abs(dCenter) >= tol && Math.abs(dCenter) <= _state.ghostRangeMm) {
      const fade = 1 - Math.abs(dCenter) / _state.ghostRangeMm;   // ближние ярче
      const op = (0.15 + 0.35 * fade).toFixed(2);
      let gpath = '';
      for (let i = 0; i < nPts; i++) {
        const cv = vp.worldToCanvas(pts[i]);
        if (!cv) { gpath = ''; break; }
        gpath += (i === 0 ? 'M' : 'L') + cv[0].toFixed(1) + ' ' + cv[1].toFixed(1);
      }
      if (gpath) {
        out += `<path d="${gpath}Z" fill="none" stroke="${rgb}" stroke-width="1" ` +
               `stroke-dasharray="3,3" opacity="${op}"/>`;
      }
      continue;
    }

    if (parallel > 0.9 && Math.abs(dCenter) < tol) {
      // ── Контур лежит в плоскости среза → рисуем его целиком ──
      let path = '';
      for (let i = 0; i < nPts; i++) {
        const cv = vp.worldToCanvas(pts[i]);
        if (!cv) { path = ''; break; }
        path += (i === 0 ? 'M' : 'L') + cv[0].toFixed(1) + ' ' + cv[1].toFixed(1);
      }
      if (path) out += `<path d="${path}Z" fill="${fill}" stroke="${rgb}" stroke-width="1.8"/>`;
      continue;
    }

    if (!_state.showCrossSection || parallel > 0.9) continue;

    // ── Плоскость среза пересекает контур поперёк → ищем точки пересечения ──
    const d = new Array(nPts);
    for (let i = 0; i < nPts; i++) d[i] = _dot(_sub(pts[i], P), N);

    const dir = c.normal ? _norm(_cross(N, c.normal)) : null;
    const hits = [];
    for (let i = 0; i < nPts; i++) {
      const j = (i + 1) % nPts;
      const di = d[i], dj = d[j];
      if ((di > 0 && dj > 0) || (di < 0 && dj < 0)) continue;
      if (di === dj) continue;
      const t = di / (di - dj);
      const p = [
        pts[i][0] + t * (pts[j][0] - pts[i][0]),
        pts[i][1] + t * (pts[j][1] - pts[i][1]),
        pts[i][2] + t * (pts[j][2] - pts[i][2]),
      ];
      hits.push({ p, key: dir ? _dot(p, dir) : 0 });
    }
    if (hits.length < 2) continue;
    hits.sort((a, b) => a.key - b.key);
    if (hits.length !== 2) allPaired = false;

    // Ось «стопки» — вдоль неё контуры идут срез за срезом
    const stackAxis = dir ? _norm(_cross(N, dir)) : null;
    const mid = [
      (hits[0].p[0] + hits[hits.length - 1].p[0]) / 2,
      (hits[0].p[1] + hits[hits.length - 1].p[1]) / 2,
      (hits[0].p[2] + hits[hits.length - 1].p[2]) / 2,
    ];
    crossSegs.push({ hits, order: stackAxis ? _dot(mid, stackAxis) : 0 });
  }

  if (crossSegs.length) {
    if (allPaired && crossSegs.length >= 2) {
      // [1.1] Сшиваем сечения соседних срезов в ЗАМКНУТЫЙ контур —
      // вместо россыпи пунктирных отрезков получается силуэт структуры,
      // как на планирующих станциях.
      crossSegs.sort((a, b) => a.order - b.order);
      const left = [], right = [];
      for (const s of crossSegs) {
        const a = vp.worldToCanvas(s.hits[0].p);
        const b = vp.worldToCanvas(s.hits[1].p);
        if (!a || !b) { left.length = 0; break; }
        left.push(a); right.push(b);
      }
      if (left.length >= 2) {
        let path = '';
        left.forEach((p, i) => { path += (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ' ' + p[1].toFixed(1); });
        for (let i = right.length - 1; i >= 0; i--) {
          path += 'L' + right[i][0].toFixed(1) + ' ' + right[i][1].toFixed(1);
        }
        out += `<path d="${path}Z" fill="${fill}" stroke="${rgb}" stroke-width="1.8"/>`;
        return out;
      }
    }
    // Фолбэк: структура сложной формы — рисуем отрезки сечений как есть
    let seg = '';
    for (const s of crossSegs) {
      for (let k = 0; k + 1 < s.hits.length; k += 2) {
        const a = vp.worldToCanvas(s.hits[k].p);
        const b = vp.worldToCanvas(s.hits[k + 1].p);
        if (!a || !b) continue;
        seg += `M${a[0].toFixed(1)} ${a[1].toFixed(1)}L${b[0].toFixed(1)} ${b[1].toFixed(1)}`;
      }
    }
    if (seg) out += `<path d="${seg}" fill="none" stroke="${rgb}" stroke-width="1.8"/>`;
  }
  return out;
}

function _drawViewport(vpId) {
  try {
    const re = getRenderingEngine();
    const vp = re?.getViewport(vpId);
    const el = vp?.element;
    if (!vp || !el) return;
    // 3D-окна и вьюпорты без проекции пропускаем
    if (String(vp.type || '').toLowerCase().includes('3d')) return;
    if (typeof vp.worldToCanvas !== 'function' || typeof vp.getCamera !== 'function') return;

    const svg = _svgFor(el);
    if (!_state.loaded || !_state.structures.length) { svg.innerHTML = ''; return; }

    const W = el.clientWidth, H = el.clientHeight;
    if (!W || !H) return;
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);

    const cam = vp.getCamera() || {};
    const N = cam.viewPlaneNormal ? _norm(cam.viewPlaneNormal) : [0, 0, 1];
    const P = cam.focalPoint || [0, 0, 0];

    let out = '';
    for (const st of _state.structures) {
      if (!st.visible) continue;
      out += _drawStructure(vp, st, N, P);
    }
    svg.innerHTML = out;
  } catch (e) {
    log.warn('[RTSTRUCT] отрисовка:', e.message);
  }
}

/** Навесить/перерисовать оверлей структур на все 2D-окна текущей раскладки. */
export function renderRTStructOverlays() {
  try {
    const re = getRenderingEngine();
    for (const vpId of (getViewportIds() || [])) {
      const vp = re?.getViewport(vpId);
      const el = vp?.element;
      if (!el || String(vp?.type || '').toLowerCase().includes('3d')) continue;
      _drawViewport(vpId);
      if (!el._rtBound) {
        el._rtBound = true;
        const redraw = () => _drawViewport(vpId);
        el.addEventListener(cornerstone.Enums.Events.CAMERA_MODIFIED, redraw);
        el.addEventListener(cornerstone.Enums.Events.IMAGE_RENDERED, redraw);
        try { new ResizeObserver(redraw).observe(el); } catch (e) { /* нет RO */ }
      }
    }
  } catch (e) {
    log.warn('[RTSTRUCT] refresh:', e.message);
  }
}

// ═══════════════════════════════════════════════════════════════
// УПРАВЛЕНИЕ ВИДИМОСТЬЮ
// ═══════════════════════════════════════════════════════════════

export function setStructureVisible(roiNumber, visible) {
  const st = _state.structures.find(s => s.roiNumber === roiNumber);
  if (st) { st.visible = !!visible; renderRTStructOverlays(); }
}

export function getStructures() {
  return _state.structures.map(s => ({
    roiNumber: s.roiNumber, name: s.name, type: s.type,
    color: s.color, visible: s.visible, contourCount: s.contourCount,
    volumeMl: s.volumeMl, zRange: s.zRange,
  }));
}

/**
 * [2.0] Прямой доступ к структурам вместе с геометрией контуров —
 * нужен модулю Этапа 2 (расчёт объёма, отступ, экспорт RTSTRUCT).
 */
export function getStructuresRaw() {
  return _state.structures;
}

/**
 * [2.0] Добавить структуру (из сегментации, а не из файла).
 * Если структура с таким roiNumber уже есть — заменяется.
 */
export function addStructure(st) {
  if (!st || !st.contours?.length) return null;
  const i = _state.structures.findIndex(s => s.roiNumber === st.roiNumber);
  if (i >= 0) _state.structures[i] = st;
  else _state.structures.push(st);
  _state.loaded = true;
  _renderPanel();
  renderRTStructOverlays();
  // [2.7] Пересчитать блок результата — объём должен обновляться сразу
  import('./rtReportModule.js').then(m => m.refreshReport?.()).catch(() => {});
  return st;
}

/** [2.0] Удалить структуру по номеру ROI. */
export function removeStructure(roiNumber) {
  const i = _state.structures.findIndex(s => s.roiNumber === roiNumber);
  if (i < 0) return false;
  _state.structures.splice(i, 1);
  if (!_state.structures.length) _state.loaded = false;
  _clearAllSvg();
  _renderPanel();
  renderRTStructOverlays();
  import('./rtReportModule.js').then(m => m.refreshReport?.()).catch(() => {});
  return true;
}

// ═══════════════════════════════════════════════════════════════
// ДЕМО-РЕЖИМ (проверка отрисовки без внешнего RTSTRUCT-файла)
// ═══════════════════════════════════════════════════════════════

/**
 * Создаёт синтетическую структуру-сферу в центре текущего тома и рисует её.
 * Нужна, чтобы проверить отрисовку контуров во всех окнах MPR БЕЗ реального
 * RTSTRUCT-файла (изолирует рендер от парсера dcmjs).
 * Вызов из консоли:  MikhmelViewer.rtStructDemo()
 * @param {{radius?:number, zStep?:number, center?:number[]}} [opts]
 */
const _demo = { center: null, radius: 20, placing: false };

export function generateDemoStructure(opts = {}) {
  const re = getRenderingEngine();
  const ids = getViewportIds() || [];
  let center = opts.center || null;
  if (!center) {
    for (const id of ids) {
      const vp = re?.getViewport(id);
      if (vp && typeof vp.getCamera === 'function' && !String(vp.type || '').toLowerCase().includes('3d')) {
        const c = vp.getCamera();
        if (c?.focalPoint) { center = c.focalPoint; break; }
      }
    }
  }
  if (!center) center = [0, 0, 0];

  const R = opts.radius || _demo.radius || 20;   // радиус сферы, мм
  const zStep = opts.zStep || 2.0;               // шаг между аксиальными контурами, мм
  const nz = Math.round(R / zStep);
  const seg = 48;                                 // точек на окружность
  const contours = [];
  for (let iz = -nz; iz <= nz; iz++) {
    const z = center[2] + iz * zStep;
    const frac = (iz * zStep) / R;
    const rr = R * Math.sqrt(Math.max(0, 1 - frac * frac)); // радиус среза сферы
    if (rr < 1) continue;
    const pts = [];
    for (let k = 0; k < seg; k++) {
      const a = (2 * Math.PI * k) / seg;
      pts.push([center[0] + rr * Math.cos(a), center[1] + rr * Math.sin(a), z]);
    }
    contours.push({
      points: pts, normal: [0, 0, 1],
      centroid: [center[0], center[1], z],
      geoType: 'CLOSED_PLANAR',
    });
  }

  // Объём сферы, мл (для сверки с измерением опухоли)
  const volumeMl = (4 / 3) * Math.PI * Math.pow(R, 3) / 1000;

  _demo.center = [center[0], center[1], center[2]];
  _demo.radius = R;

  _state.structures = [{
    roiNumber: 999, name: `DEMO Сфера R=${R}мм · ${volumeMl.toFixed(2)} мл`, type: 'GTV',
    color: [80, 255, 120], visible: true,
    contours, contourCount: contours.length,
    spacing: zStep,
    isDemo: true,
    zRange: [center[2] - R, center[2] + R],
  }];
  _state.frameOfReferenceUID = 'DEMO';
  _state.loaded = true;
  log.info(`[RTSTRUCT] Демо-сфера: R=${R}мм, V=${volumeMl.toFixed(2)}мл, центр=[${center.map(v => v.toFixed(1)).join(', ')}]`);
  _renderPanel();
  renderRTStructOverlays();
  return _state.structures[0];
}

/** Сдвинуть демо-сферу на вектор (мм) в мировых координатах. */
export function moveDemoStructure(dx = 0, dy = 0, dz = 0) {
  if (!_demo.center) return null;
  return generateDemoStructure({
    center: [_demo.center[0] + dx, _demo.center[1] + dy, _demo.center[2] + dz],
    radius: _demo.radius,
  });
}

/** Задать радиус демо-сферы (мм), центр сохраняется. */
export function setDemoRadius(mm) {
  const R = Math.max(2, Math.min(80, Number(mm) || 20));
  return generateDemoStructure({ center: _demo.center || undefined, radius: R });
}

/**
 * Режим установки кликом: следующий клик по любому 2D-окну поставит
 * центр демо-сферы в эту точку (мировые координаты пациента).
 */
let _placeDelegationBound = false;

/** Найти вьюпорт по элементу под курсором (устойчиво к смене раскладки). */
function _viewportAtEvent(e) {
  const re = getRenderingEngine();
  if (!re) return null;
  try {
    for (const vp of re.getViewports()) {
      if (vp?.element && vp.element.contains(e.target)) return vp;
    }
  } catch (err) { /* пусто */ }
  return null;
}

function _onPlaceClick(e) {
  if (!_demo.placing) return;
  const vp = _viewportAtEvent(e);
  if (!vp || String(vp.type || '').toLowerCase().includes('3d')) return;
  if (typeof vp.canvasToWorld !== 'function') return;
  try {
    const rect = vp.element.getBoundingClientRect();
    const world = vp.canvasToWorld([e.clientX - rect.left, e.clientY - rect.top]);
    if (!world) return;
    e.preventDefault();
    e.stopPropagation();
    generateDemoStructure({ center: [world[0], world[1], world[2]], radius: _demo.radius });
    armPlaceByClick(false);
    _toast(`🎯 Сфера установлена в окне "${vp.id}": [${world.map(v2 => v2.toFixed(1)).join(', ')}] мм`, 'success');
    _syncDemoControls();
  } catch (err) { log.warn('[RTSTRUCT] place:', err.message); }
}

export function armPlaceByClick(on = true) {
  _demo.placing = !!on;
  // [1.2] Делегирование на контейнере вместо листенеров на каждом окне —
  // переживает пересоздание раскладки.
  if (!_placeDelegationBound) {
    const container = document.getElementById('viewport-container');
    if (container) {
      container.addEventListener('click', _onPlaceClick, true);
      _placeDelegationBound = true;
    }
  }
  try {
    const re = getRenderingEngine();
    for (const vpId of (getViewportIds() || [])) {
      const el = re?.getViewport(vpId)?.element;
      if (el) el.style.cursor = _demo.placing ? 'crosshair' : '';
    }
  } catch (err) { /* пусто */ }
  _syncDemoControls();
  return _demo.placing;
}

// ═══════════════════════════════════════════════════════════════
// UI: панель структур + импорт
// ═══════════════════════════════════════════════════════════════

function _renderPanel() {
  let panel = document.getElementById('rtstruct-panel');
  if (!panel) return; // панель ещё не смонтирована
  const list = panel.querySelector('#rtstruct-list');
  const count = panel.querySelector('#rtstruct-count');
  if (count) count.textContent = String(_state.structures.length);

  if (!_state.structures.length) {
    list.innerHTML = '<div class="rt-note" style="margin-top:0;">Структуры не загружены. Нажмите «Импорт RTSTRUCT».</div>';
    return;
  }

  list.innerHTML = _state.structures.map(s => {
    const c = `rgb(${s.color[0]},${s.color[1]},${s.color[2]})`;
    const badge = s.type ? `<span class="rt-struct-type">${s.type}</span>` : '';
    return `<label class="rt-struct">
      <input type="checkbox" data-roi="${s.roiNumber}" ${s.visible ? 'checked' : ''} style="accent-color:${c};">
      <span class="rt-struct-dot" style="background:${c};"></span>
      <span class="rt-struct-name">${s.name}${badge}</span>
      ${s.volumeMl > 0 ? `<b class="rt-struct-vol">${s.volumeMl.toFixed(2)} мл</b>` : ''}
      <span class="rt-struct-n">${s.contourCount}</span>
      <button data-del="${s.roiNumber}" class="rt-del" title="Удалить структуру">×</button>
    </label>`;
  }).join('');

  list.querySelectorAll('input[type=checkbox]').forEach(cb => {
    cb.addEventListener('change', () => setStructureVisible(Number(cb.dataset.roi), cb.checked));
  });
  list.querySelectorAll('button[data-del]').forEach(b => {
    b.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      removeStructure(Number(b.dataset.del));
    });
  });
}

/**
 * [7.0.32] Вставить кнопку RT в верхнюю панель. Класс/оформление берём у
 * «✅ Протокол» (та же голая кнопка-действие .tb-dropdown-trigger без обёртки
 * .tb-dropdown), стрелку дропдауна глушим тем же ::after{content:none}. Панель
 * строится асинхронно — дожимаем интервалом; если тулбара нет вовсе — честный
 * фолбэк на прежнюю плавающую кнопку (чтобы функциональность не пропала).
 */
function _mountLauncherInToolbar(btn) {
  const place = () => {
    const anchor = document.getElementById('btn-mrq-check') || document.getElementById('btn-protocol-check');
    if (!anchor || !anchor.parentNode) return false;
    btn.className = anchor.className || 'tb-dropdown-trigger';
    btn.style.flexShrink = '0';
    anchor.parentNode.insertBefore(btn, anchor.nextSibling);   // после «Качество МРТ»/«Протокол»
    if (!document.getElementById('rt-launcher-css')) {
      const s = document.createElement('style');
      s.id = 'rt-launcher-css';
      s.textContent = '#rtstruct-launcher::after{content:none}';
      document.head.appendChild(s);
    }
    return true;
  };
  if (place()) return;
  let tries = 0;
  const t = setInterval(() => {
    if (place() || ++tries > 20) {
      clearInterval(t);
      if (tries > 20 && !btn.isConnected) {
        // тулбар не найден — не теряем кнопку: ставим плавающую, как раньше
        btn.className = '';
        btn.setAttribute('style',
          'position:fixed;left:12px;bottom:120px;z-index:99998;padding:8px 12px;'
          + 'background:#0d1520;color:#7fd;border:1px solid #0af;border-radius:8px;'
          + 'font-size:13px;cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,.5);');
        document.body.appendChild(btn);
        log.warn('[RTSTRUCT] Верхняя панель не найдена — кнопка RT осталась плавающей.');
      }
    }
  }, 500);
}

/* ══════════════════════════════════════════════════════════════════════
   [12.0.16] ПАНЕЛЬ RT СТАЛА РАЗДЕЛОМ ПРАВОГО САЙДБАРА.

   Раньше панель висела плавающим блоком (position:fixed; left:12px;
   bottom:160px; z-index:99999) и ложилась поверх ЛЕВОГО сайдбара —
   списка серий и сведений о пациенте. Теперь она переезжает в раздел
   #tab-rt правой панели, а кнопка RT в верхнем меню этот раздел
   открывает и делает активным.

   [12.0.25] Постоянный жёлтый перелив по краю раздела снят. Открытие
   идёт через общий ui/rightSection.js: раздел прокручивается к началу, а
   его рамка и вкладка один раз коротко вспыхивают — видно, что открылось
   справа, и больше ничего не движется. Кнопка RT «нажата», пока раздел
   на виду: за этим следит наблюдатель состояния (следитьЗаКнопкой).

   Содержимое панели и все идентификаторы внутри не тронуты: к
   #rtstruct-panel и #rtstruct-list пристраиваются модули дозы, DVH,
   изоцентра, сегментации, отрисовки, протокольного шлюза, отчётов и
   совмещения. Переезжает контейнер, не его внутренности.

   На узком экране (≤768px) правого сайдбара нет — там панель
   по-прежнему работает плавающей, как и работала.
   ══════════════════════════════════════════════════════════════════════ */

let _RT_FLOAT_STYLE = '';
let _rtHomeTimer = null;

/** Узел раздела в правом сайдбаре; на телефоне сайдбара нет — там null. */
function _rtHost() {
  if (window.innerWidth <= 768) return null;
  return document.getElementById('tab-rt');
}

/** Панель стоит в разделе, а не висит плавающей? */
function _rtEmbedded(panel) {
  return !!(panel && panel.parentElement && panel.parentElement.id === 'tab-rt');
}

/** Поставить панель туда, где ей место при нынешней ширине экрана. */
function _rtPlace(panel) {
  const host = _rtHost();
  if (host) {
    if (panel.parentElement !== host) host.appendChild(panel);
    // Плавающие координаты и собственная рамка в разделе только мешают:
    // ширину, фон и прокрутку задаёт сам сайдбар (правило #tab-rt
    // #rtstruct-panel в index.html).
    panel.removeAttribute('style');
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';
    const stub = document.getElementById('rt-tab-empty');
    if (stub) stub.style.display = 'none';
  } else if (panel.parentElement !== document.body) {
    document.body.appendChild(panel);
    if (_RT_FLOAT_STYLE) panel.setAttribute('style', _RT_FLOAT_STYLE);
  }
}

function _rtSidebarOpen() {
  const sb = document.getElementById('sidebar-right');
  return !!(sb && sb.classList.contains('floating-open'));
}

/** Раздел RT сейчас на виду? */
function _rtVisible(panel) {
  if (_rtEmbedded(panel)) {
    const tab = document.getElementById('tab-rt');
    return _rtSidebarOpen() && !!tab && tab.classList.contains('active');
  }
  return panel.style.display !== 'none';
}

/** Подсветка кнопки RT в верхнем меню, пока раздел открыт. */
function _rtLive(on) {
  const b = document.getElementById('rtstruct-launcher');
  if (b) b.classList.toggle('rt-live', !!on);
}

/** Открыть раздел: выдвинуть правый сайдбар и сделать вкладку RT активной. */
function _rtOpen(panel) {
  _rtPlace(panel);
  if (!_rtEmbedded(panel)) { panel.style.display = 'flex'; _rtLive(true); return; }
  // [12.0.25] Через общий модуль: те же кнопки сайдбара и вкладки, что и
  // прежде (один хозяин у состояния), плюс прокрутка к началу и отметка.
  // Подсветку кнопки RT здесь не ставим — её ведёт наблюдатель состояния.
  открытьРазделСправа('tab-rt');
}

function _rtClose(panel) {
  if (!_rtEmbedded(panel)) { panel.style.display = 'none'; _rtLive(false); return; }
  закрытьСайдбарСправа();
}

function _mountUI() {
  if (document.getElementById('rtstruct-panel')) return;

  // [7.0.32] Кнопка-лаунчер — в ВЕРХНЕЙ панели рядом с «Протокол»/«Качество МРТ»
  // (просьба Андрея: слева она болталась поверх снимка). Иконка — SVG из спрайта
  // приложения (#i-radiation), класс .ico — тот же размер/цвет/штрих, что у соседей.
  const btn = document.createElement('button');
  btn.id = 'rtstruct-launcher';
  btn.title = 'RT Structure Set — импорт и показ контуров, доза, DVH, изоцентр, совмещение';
  btn.innerHTML = '<svg class="ico" aria-hidden="true"><use href="#i-radiation"/></svg> RT';

  // Панель
  const panel = document.createElement('div');
  panel.id = 'rtstruct-panel';
  panel.setAttribute('style',
    // Запасной (плавающий) вид — только для узкого экрана, где правого
    // сайдбара нет. В сайдбаре эти координаты снимаются в _rtPlace().
    'position:fixed;left:12px;bottom:160px;z-index:99999;width:270px;max-height:70vh;' +
    'display:none;flex-direction:column;background:#0d1520;color:#fff;' +
    'border:1px solid #0af;border-radius:10px;box-shadow:0 10px 40px rgba(0,0,0,.7);' +
    'font-family:system-ui,sans-serif;overflow-y:auto;overflow-x:hidden;');
  panel.innerHTML = `
    <div class="rt-sec">
      <div class="rt-sec-head" style="margin-bottom:0;">
        <span class="rt-sec-title">RT структуры (<span id="rtstruct-count">0</span>)</span>
        <span id="rtstruct-ver" class="rt-ver"></span>
        <button id="rtstruct-close" class="rt-modal-x" title="Закрыть панель" aria-label="Закрыть панель">×</button>
      </div>
    </div>
    <div class="rt-sec">
      <!-- [12.0.19] Импорт — отдельной строкой во всю ширину. В одной строке с
           «Очистить» он сплющивался до нечитаемого огрызка на узкой панели, а
           панель узкая всегда, когда врач сузил правый сайдбар. Парой идут
           кнопки одного веса — демо и очистка. -->
      <button id="rtstruct-import" class="rt-btn rt-btn--primary" title="Загрузить файл RTSTRUCT из станции планирования">Импорт RTSTRUCT</button>
      <div class="rt-row">
        <button id="rtstruct-demo" class="rt-btn rt-btn--sm" title="Демо-тест: сфера без файла — проверить, что объём и отрисовка работают">Демо-тест</button>
        <button id="rtstruct-clear" class="rt-btn rt-btn--sm rt-btn--danger" title="Удалить все загруженные структуры">Очистить</button>
      </div>
      <div id="rtstruct-demo-ctl" style="display:none;">
        <button id="rtstruct-place" class="rt-btn rt-btn--sm">Поставить кликом по опухоли</button>
        <div class="rt-slider">
          <span class="rt-slider-label">Радиус</span>
          <input id="rtstruct-radius" type="range" min="2" max="60" step="1" value="20">
          <span id="rtstruct-radius-val" class="rt-slider-val">20 мм</span>
        </div>
        <div class="rt-field">
          <span>Сдвиг, мм</span>
          <div class="rt-row" style="margin-top:0;">
            <button class="rt-nudge rt-btn rt-btn--sm" data-ax="0" data-d="-1" title="Вправо/влево (X−)">X−</button>
            <button class="rt-nudge rt-btn rt-btn--sm" data-ax="0" data-d="1"  title="X+">X+</button>
            <button class="rt-nudge rt-btn rt-btn--sm" data-ax="1" data-d="-1" title="Y−">Y−</button>
            <button class="rt-nudge rt-btn rt-btn--sm" data-ax="1" data-d="1"  title="Y+">Y+</button>
            <button class="rt-nudge rt-btn rt-btn--sm" data-ax="2" data-d="-1" title="Z− (срез ниже)">Z−</button>
            <button class="rt-nudge rt-btn rt-btn--sm" data-ax="2" data-d="1"  title="Z+ (срез выше)">Z+</button>
          </div>
        </div>
        <div id="rtstruct-demo-pos" class="rt-note rt-mono"></div>
      </div>
    </div>
    <div id="rtstruct-list" class="rt-sec"></div>
    <input id="rtstruct-file" type="file" accept=".dcm,application/dicom,*/*" style="display:none;">`;

  _mountLauncherInToolbar(btn);
  document.body.appendChild(panel);

  // [12.0.16] Запасной (плавающий) вид запоминаем — он нужен на телефоне,
  // где правого сайдбара нет, и если раздела #tab-rt в разметке не окажется.
  _RT_FLOAT_STYLE = panel.getAttribute('style') || '';
  _rtPlace(panel);
  window.addEventListener('resize', () => {
    clearTimeout(_rtHomeTimer);
    _rtHomeTimer = setTimeout(() => _rtPlace(panel), 250);
  });

  // Штамп версии в шапке панели — видно, что загружена свежая сборка
  try {
    const appVer = (typeof globalThis !== 'undefined' && globalThis.MikhmelViewer?.version) || '';
    const verEl = panel.querySelector('#rtstruct-ver');
    if (verEl) {
      verEl.textContent = appVer ? `v${appVer} · RT ${RT_MODULE_VERSION.split(' ')[0]}` : `RT ${RT_MODULE_VERSION.split(' ')[0]}`;
      verEl.title = `Приложение v${appVer} · Модуль СРХ ${RT_MODULE_VERSION}`;
    }
  } catch (e) { /* пусто */ }

  const toggle = () => {
    if (_rtVisible(panel)) _rtClose(panel);
    else _rtOpen(panel);
  };
  btn.addEventListener('click', toggle);
  panel.querySelector('#rtstruct-close').addEventListener('click', () => _rtClose(panel));

  // Врач мог уйти на другую вкладку или закрыть сайдбар мимо кнопки RT —
  // тогда подсветка кнопки должна погаснуть сама.
  //
  // [12.0.25] По СОСТОЯНИЮ, а не по щелчкам. Здесь слушались щелчки по
  // рядам вкладок и по кнопке сайдбара, но кнопку сайдбара перехватывает
  // htmlInit.js в фазе захвата со stopImmediatePropagation — этот слушатель
  // не срабатывал никогда, и после закрытия сайдбара его кнопкой RT
  // оставалась гореть.
  следитьЗаКнопкой('tab-rt', btn);
  panel.querySelector('#rtstruct-clear').addEventListener('click', () => clearRTStruct());
  panel.querySelector('#rtstruct-demo').addEventListener('click', () => {
    try {
      const s = generateDemoStructure();
      panel.querySelector('#rtstruct-demo-ctl').style.display = 'block';
      _syncDemoControls();
      _toast(`🧪 Демо: сфера, ${s.contourCount} контуров. Двигайте её кнопкой «Поставить кликом».`, 'success');
    } catch (e) { _toast('❌ Демо не удалось: ' + e.message, 'error'); }
  });

  panel.querySelector('#rtstruct-place').addEventListener('click', () => {
    const on = armPlaceByClick(!_demo.placing);
    _toast(on ? '🎯 Кликните по опухоли в любом окне' : 'Режим установки выключен', 'info');
  });

  const radius = panel.querySelector('#rtstruct-radius');
  radius.addEventListener('input', () => {
    panel.querySelector('#rtstruct-radius-val').textContent = `${radius.value} мм`;
  });
  radius.addEventListener('change', () => { setDemoRadius(radius.value); _syncDemoControls(); });

  panel.querySelectorAll('.rt-nudge').forEach(b => {
    b.addEventListener('click', () => {
      const ax = Number(b.dataset.ax), d = Number(b.dataset.d);
      const step = [0, 0, 0]; step[ax] = d * 1;   // шаг 1 мм
      moveDemoStructure(step[0], step[1], step[2]);
      _syncDemoControls();
    });
  });

  const fileInput = panel.querySelector('#rtstruct-file');
  panel.querySelector('#rtstruct-import').addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', async () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    try {
      const structs = await loadRTStructFromFile(f);
      _toast(`✅ RTSTRUCT загружен: ${structs.length} структур`, 'success');
    } catch (e) {
      log.error('[RTSTRUCT] импорт:', e);
      _toast(`❌ Ошибка чтения RTSTRUCT: ${e.message}`, 'error');
    }
    fileInput.value = '';
  });

  _renderPanel();
}

/** Обновить подписи в блоке управления демо-сферой */
function _syncDemoControls() {
  const panel = document.getElementById('rtstruct-panel');
  if (!panel) return;
  const ctl = panel.querySelector('#rtstruct-demo-ctl');
  if (ctl && _demo.center) ctl.style.display = 'block';
  const r = panel.querySelector('#rtstruct-radius');
  const rv = panel.querySelector('#rtstruct-radius-val');
  if (r) r.value = String(_demo.radius);
  if (rv) rv.textContent = `${_demo.radius} мм`;
  const pos = panel.querySelector('#rtstruct-demo-pos');
  if (pos && _demo.center) {
    const V = (4 / 3) * Math.PI * Math.pow(_demo.radius, 3) / 1000;
    pos.textContent = `центр [${_demo.center.map(v => v.toFixed(1)).join(', ')}] мм · V=${V.toFixed(2)} мл`;
  }
  const pb = panel.querySelector('#rtstruct-place');
  if (pb) {
    pb.style.background = _demo.placing ? '#fd0' : '#f80';
    pb.textContent = _demo.placing ? '⏳ Кликните по опухоли…' : '🎯 Поставить кликом по опухоли';
  }
}

function _toast(msg, type) {
  try {
    // helpers.showToast загружается лениво — не тянем зависимость жёстко
    import('../utils/helpers.js').then(h => h.showToast?.(msg, type, 3000)).catch(() => {});
  } catch (e) { /* пусто */ }
}

// ═══════════════════════════════════════════════════════════════
// ИНИЦИАЛИЗАЦИЯ
// ═══════════════════════════════════════════════════════════════

export function initRTStruct() {
  try {
    _mountUI();
    registerAPIs({
      loadRTStruct: loadRTStructFromFile,
      loadRTStructBuffer: loadRTStructFromArrayBuffer,
      clearRTStruct,
      getRTStructures: getStructures,
      setRTStructVisible: setStructureVisible,
      refreshRTStruct: renderRTStructOverlays,
      rtStructDemo: generateDemoStructure,
      rtStructMove: moveDemoStructure,
      rtStructRadius: setDemoRadius,
      rtStructPlaceByClick: armPlaceByClick,
    });

    // [1.1] При смене раскладки Cornerstone пересоздаёт DOM вьюпортов —
    // старые привязки теряются, и структуры перестают рисоваться в новых окнах.
    // Перепривязываемся с небольшой задержкой (окна создаются асинхронно).
    const rebind = () => setTimeout(() => {
      renderRTStructOverlays();
      if (_demo.placing) armPlaceByClick(true);
    }, 300);
    for (const evt of [EVENTS.LAYOUT_CHANGED, EVENTS.VIEWPORT_CREATED, EVENTS.STACK_LOADED, EVENTS.VOLUME_LOAD_DONE]) {
      try { if (evt) onEvent(evt, rebind); } catch (e) { /* события может не быть */ }
    }
    window.addEventListener('resize', () => renderRTStructOverlays());
    // Справочник — первым, чтобы значки «?» работали сразу
    import('./rtKnowledgeModule.js')
      .then(m => m.initRTKnowledge())
      .catch(e => log.warn('[RTSTRUCT] Справочник не загрузился:', e.message));

    // [2.0] Этап 2 монтируется в ту же панель — после того, как она создана
    import('./rtSegmentModule.js')
      .then(m => m.initRTSegment())
      .catch(e => log.warn('[RTSTRUCT] Модуль Этапа 2 не загрузился:', e.message));

    log.info(`[RTSTRUCT] Модуль инициализирован — ${RT_MODULE_VERSION}`);
  } catch (e) {
    log.error('[RTSTRUCT] init failed:', e);
  }
}

export default { initRTStruct, loadRTStructFromFile, parseRTStruct, getStructures };
